import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { CustomTypography } from 'src/components/CustomTypography';

import { MESSAGING_BANK_ASSET } from '../../utils';
import { EmptyMenuState } from '../EmptyMenuState';

import { useStyles } from './useStyles';

export const SearchResults = ({
  resourcesTypeFilter,
  resourcesTagFilter,
  urlsMenu,
  documentMenu,
  messagingBankMenu,
  imagesMenu,
  urlResources,
  documentResources,
  imageResources,
  bankItems,
  searchValue,
}) => {
  const styles = useStyles();

  const urlsMenuRef = useRef(null);
  const documentMenuRef = useRef(null);
  const messagingBankMenuRef = useRef(null);
  const imagesMenuRef = useRef(null);

  const [focusedIndex, setFocusedIndex] = useState(-1);

  const urlsItemsLength = urlsMenuRef.current?.children?.length || 0;
  const documentsItemsLength = documentMenuRef.current?.children?.length || 0;
  const bankItemsLength = messagingBankMenuRef.current?.children?.length || 0;
  const imagesItemsLength = imagesMenuRef.current?.children?.length || 0;

  const totalItemsLength =
    urlsItemsLength + documentsItemsLength + bankItemsLength + imagesItemsLength;

  const handleItemClick = useCallback(
    (itemIndex) => {
      if (itemIndex < urlsItemsLength) {
        urlsMenuRef.current?.children[itemIndex]?.click();
      } else if (itemIndex < urlsItemsLength + documentsItemsLength) {
        documentMenuRef.current?.children[itemIndex - urlsItemsLength]?.click();
      } else if (itemIndex < urlsItemsLength + documentsItemsLength + bankItemsLength) {
        messagingBankMenuRef.current?.children[
          itemIndex - urlsItemsLength - documentsItemsLength
        ]?.click();
      } else {
        imagesMenuRef.current?.children[
          itemIndex - urlsItemsLength - documentsItemsLength - bankItemsLength
        ]?.click();
      }
    },
    [urlsItemsLength, documentsItemsLength, bankItemsLength]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (!totalItemsLength) {
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setFocusedIndex((prevIndex) => (prevIndex === totalItemsLength ? 0 : prevIndex + 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setFocusedIndex((prevIndex) =>
          [0, -1].includes(prevIndex) ? totalItemsLength : prevIndex - 1
        );
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (focusedIndex === -1) {
          handleItemClick(0);
        } else {
          handleItemClick(focusedIndex);
        }
      }
    },
    [focusedIndex, totalItemsLength, handleItemClick]
  );

  useEffect(() => {
    const handleDocumentKeyDown = (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter') {
        handleKeyDown(event);
      }
    };
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (focusedIndex < urlsItemsLength) {
      urlsMenuRef.current?.children[focusedIndex]?.focus();
    } else if (focusedIndex < urlsItemsLength + documentsItemsLength) {
      documentMenuRef.current?.children[focusedIndex - urlsItemsLength]?.focus();
    } else if (focusedIndex < urlsItemsLength + documentsItemsLength + bankItemsLength) {
      messagingBankMenuRef.current?.children[
        focusedIndex - urlsItemsLength - documentsItemsLength
      ]?.focus();
    } else {
      imagesMenuRef.current?.children[
        focusedIndex - urlsItemsLength - documentsItemsLength - bankItemsLength
      ]?.focus();
    }
  }, [focusedIndex, urlsItemsLength, documentsItemsLength, bankItemsLength, imagesItemsLength]);

  useEffect(() => {
    if (focusedIndex > totalItemsLength) {
      setFocusedIndex(-1);
    }
  }, [focusedIndex, totalItemsLength]);

  const displayUrls = urlResources?.length > 0;
  const displayDocuments = documentResources?.length > 0;
  const displayImages = imageResources?.length > 0;
  const displayBankMessages =
    bankItems?.length > 0 &&
    !bankItems[0]?.emptyState &&
    (!resourcesTypeFilter?.length || resourcesTypeFilter.includes(MESSAGING_BANK_ASSET)) &&
    resourcesTagFilter.length === 0;

  return (
    <>
      <div>
        {displayUrls && (
          <>
            <CustomTypography className={styles.menuHeader}>Webpages</CustomTypography>
            <div ref={urlsMenuRef}>{urlsMenu}</div>
          </>
        )}
        {displayDocuments && (
          <>
            <CustomTypography className={styles.menuHeader}>Files</CustomTypography>
            <div ref={documentMenuRef}>{documentMenu}</div>
          </>
        )}
        {displayBankMessages && (
          <>
            <CustomTypography className={styles.menuHeader}>Messaging bank assets</CustomTypography>
            <div ref={messagingBankMenuRef}>{messagingBankMenu}</div>
          </>
        )}
        {displayImages && (
          <>
            <CustomTypography className={styles.menuHeader}>Images</CustomTypography>
            <div
              className={styles.menuListImage}
              ref={imagesMenuRef}
            >
              {imagesMenu}
            </div>
          </>
        )}
        {!displayUrls && !displayDocuments && !displayImages && !displayBankMessages && (
          <EmptyMenuState searchText={searchValue} />
        )}
      </div>
    </>
  );
};

SearchResults.propTypes = {
  resourcesTypeFilter: PropTypes.arrayOf(PropTypes.string),
  resourcesTagFilter: PropTypes.arrayOf(PropTypes.string),
  urlsMenu: PropTypes.node,
  documentMenu: PropTypes.node,
  messagingBankMenu: PropTypes.node,
  imagesMenu: PropTypes.node,
  urlResources: PropTypes.arrayOf(PropTypes.shape()),
  documentResources: PropTypes.arrayOf(PropTypes.shape()),
  imageResources: PropTypes.arrayOf(PropTypes.shape()),
  bankItems: PropTypes.arrayOf(PropTypes.shape()),
  searchValue: PropTypes.string,
};

SearchResults.defaultProps = {
  resourcesTypeFilter: [],
  resourcesTagFilter: [],
  urlsMenu: null,
  documentMenu: null,
  messagingBankMenu: null,
  imagesMenu: null,
  urlResources: [],
  documentResources: [],
  imageResources: [],
  bankItems: [],
  searchValue: '',
};
